import type { CarStats, CarUpgrade } from "../stats/CarStats.js";
import { DEFAULT_UPGRADES } from "../stats/CarStats.js";

// ── Sub-interfaces ─────────────────────────────────────────────────────────────

/**
 * Physics simulation constants hardcoded inside VehicleController.applyInput().
 * These affect driving feel but are separate from the per-vehicle CarStats layer.
 * Extension to the required GameConfig interface — needed to wire VehicleController.
 */
export interface PhysicsConfig {
  /** Engine-braking friction multiplier (braking × friction × dt). */
  friction: number;
  /** Turn authority at zero speed (fraction of turnRate). */
  turnFactorLow: number;
  /** Extra turn authority added linearly with speed (fraction of turnRate). */
  turnFactorHigh: number;
  /** Steering multiplier when handbrake is held. */
  handbrakeTurnBoost: number;
  /** Handbrake lateral velocity target scale. */
  driftTargetScale: number;
  /** Lateral speed lerp rate when handbraking (per second). */
  driftLateralLerp: number;
  /** Extra yaw rate applied during handbrake slides. */
  driftYawBoost: number;
  /** Grip factor used for lateral decay when NOT handbraking. */
  tractionGripFull: number;
  /** Grip factor used for lateral decay when handbraking. */
  tractionGripDrift: number;
  /** Reverse acceleration as a fraction of forward acceleration. */
  reverseAccelScale: number;
  /** Wall-collision velocity restitution (0 = full stop, 1 = full bounce). */
  wallRestitution: number;
  /** Obstacle-collision restitution scalar (applied to reflected component). */
  obstacleRestitution: number;
  /** Extra speed fraction removed at a pure head-on wall hit (0–1). 0 = no penalty. */
  wallHeadOnPenalty: number;
  /** Impact severity below which no speed penalty is applied for wall hits (0–1). */
  wallGlancingThreshold: number;
  /** Extra speed fraction removed at a pure head-on obstacle hit (0–1). 0 = no penalty. */
  obstacleHeadOnPenalty: number;
  /** Impact severity below which no speed penalty is applied for obstacle hits (0–1). */
  obstacleGlancingThreshold: number;
  /** Minimum outward speed (m/s) guaranteed after obstacle impact — prevents sticking at low speed. */
  minimumObstacleBounceMps: number;
  /**
   * Reference mass for acceleration and braking scaling (kg-scaled units).
   * effectiveAccel = accel × (referenceMass / mass)
   * effectiveBrake = brakeForce × (referenceMass / mass)
   * Vehicles at this mass feel unchanged. Set to balanced-starter.stats.mass.
   */
  referenceMass: number;
  /** Car-car collision restitution scalar (applied to reflected component). COR = carCarRestitution − 1. */
  carCarRestitution: number;
  /** Extra speed fraction removed at a pure head-on car-car hit (0–1). */
  carCarHeadOnPenalty: number;
  /** Impact severity below which no speed penalty is applied for car-car hits (0–1). */
  carCarGlancingThreshold: number;
}

export interface BoostConfig {
  maxEnergy: number;
  drainRate: number;
  regenRate: number;
  speedMultiplier: number;
  accelMultiplier: number;
}

export interface AudioConfig {
  engineMasterGain: number;
  baseRpm: number;
  idleRpm: number;
  maxRpm: number;
  loadBoost: number;
  rpmSmooth: number;
  gearUpThresholds: readonly [number, number, number];
  gearDnThresholds: readonly [number, number, number];
  gearRpmDrop: number;
  gearDipMs: number;
  gearDipGain: number;
  playbackMin: number;
  playbackMax: number;
  filterFreqIdle: number;
  filterMaxFreq: number;
  filterThrottleBoost: number;
  filterSmooth: number;
  coastGainScale: number;
  tyreRateBase: number;
  tyreRateRange: number;
  squealCooldownMs: number;
  squealDeltaThresh: number;
}

/**
 * NOT WIRED. Template values for a future per-vehicle weapon-upgrade system.
 *
 * CURRENT live weapon source:
 *   content/vehicles/<id>.json → blueprint.weapon.{damage, reloadMs, projectileSpeed}
 *   Read directly from VehicleBlueprint at runtime. No computeWeaponStats() layer exists yet.
 *
 * FUTURE intended flow (when weapon upgrades are added):
 *   blueprint.weapon  →  computeWeaponStats(blueprint, weaponUpgrades)  →  active weapon stats
 *   The host-override template here would then seed the lobby default the same way
 *   hostOverrideTemplate does for vehicle base stats.
 *
 * Weapon stat source-of-truth (today):
 *   ┌─────────────────┬──────────────────────────────────────────────────┐
 *   │ stat            │ live source                                      │
 *   ├─────────────────┼──────────────────────────────────────────────────┤
 *   │ damage          │ blueprint.weapon.damage        (from JSON)       │
 *   │ reloadMs        │ blueprint.weapon.reloadMs      (from JSON)       │
 *   │ projectileSpeed │ blueprint.weapon.projectileSpeed (from JSON)     │
 *   └─────────────────┴──────────────────────────────────────────────────┘
 */
export interface WeaponOverrideTemplate {
  damage: number;
  reloadMs: number;
  projectileSpeed: number;
}

export interface CameraConfig {
  /** Eye height as a fraction of body height. */
  firstPersonHeight: number;
  /** Eye forward offset as a fraction of body length. */
  firstPersonForward: number;
}

// ── Stat architecture — read this before editing ───────────────────────────────
//
//  LIVE RUNTIME PATH (what actually drives the vehicle):
//
//    content/vehicles/<id>.json          ← source of truth for base stats
//          │  handling.maxSpeed / acceleration / braking / traction / drift / turnRate
//          │  stats.mass
//          ▼
//    blueprintToBaseStats(blueprint)     shared/src/stats/defaultStats.ts
//          │  maps JSON fields → CarStats shape; fills boostPower/downforce/gearShiftSpeed
//          ▼
//    computeStats(blueprint, upgrades, damage)   shared/src/stats/computeStats.ts
//          │  applies upgrade multipliers then damage fractions
//          ▼
//    VehicleController.carStats          client/src/physics/VehicleController.ts
//          │  used inside applyInput() every frame
//          ▼
//    gameplay physics
//
//  WIRED CONFIG VALUES (global tuning, not per-vehicle):
//    GameConfig.vehicle.physics  → VehicleController constructor (friction, restitution, drift constants…)
//    GameConfig.boost            → boost.ts named exports
//    GameConfig.audio            → EngineAudio.init()
//    GameConfig.camera           → computeFirstPersonEye() in Game.ts (first-person eye position)
//
//  NOT WIRED — future hookpoints only:
//    GameConfig.vehicle.hostOverrideTemplate    (vehicle base stat override template)
//    GameConfig.vehicle.upgradeTemplate
//    GameConfig.hostWeaponOverrideTemplate      (weapon stat override template; live stats from blueprint.weapon)

// ── Main interface ──────────────────────────────────────────────────────────────

export interface GameConfig {
  vehicle: {
    /**
     * NOT WIRED. Template values for future lobby/host override systems —
     * e.g. a server operator who wants to force all vehicles to the same base
     * stats, or a test mode that ignores per-vehicle JSON entirely.
     *
     * Do NOT read this during normal gameplay.
     * Live base stats come from content/vehicles/<id>.json via blueprintToBaseStats().
     */
    hostOverrideTemplate: CarStats;
    /**
     * NOT WIRED. Upgrade state template for future use.
     * Active upgrades are applied in Game.ts via VehicleController.applyUpgrades()
     * using CAR_PRESETS, not this value.
     */
    upgradeTemplate: CarUpgrade;
    /**
     * WIRED into VehicleController constructor (options.physics).
     * Global physics tuning — friction, restitution, drift, turn authority.
     * These are independent of per-vehicle stats.
     */
    physics: PhysicsConfig;
  };
  /**
   * NOT WIRED. Seed values for a future weapon-upgrade system.
   * Live weapon stats come from VehicleBlueprint.weapon (content/vehicles/<id>.json).
   * Do NOT read this during normal gameplay.
   */
  hostWeaponOverrideTemplate: WeaponOverrideTemplate;
  /**
   * WIRED into boost.ts module exports (BOOST_MAX_ENERGY, BOOST_DRAIN_RATE, etc.).
   */
  boost: BoostConfig;
  /**
   * WIRED into EngineAudio.init() via optional audioConfig parameter.
   */
  audio: AudioConfig;
  /**
   * WIRED into Game.ts via computeFirstPersonEye() — drives first-person eye height and forward offset.
   */
  camera: CameraConfig;
}

// ── Default config ─────────────────────────────────────────────────────────────
// WIRED values (physics/boost/audio/camera) match current hardcoded behaviour exactly.
// NOT WIRED templates (hostOverrideTemplate, hostWeaponOverrideTemplate) mirror
// balanced-starter.json for reference only — not read at runtime.

export const DEFAULT_GAME_CONFIG: GameConfig = {
  // ── FUTURE / NOT WIRED ────────────────────────────────────────────────────────
  vehicle: {
    hostOverrideTemplate: {
      topSpeed:      30,
      accel:         16,
      brakeForce:    18,
      turnRate:       2.6,
      traction:       0.7,
      drift:          0.3,
      mass:           8,
      boostPower:     1.0,
      downforce:      0.4,
      gearShiftSpeed: 1.0,
    },
    upgradeTemplate: DEFAULT_UPGRADES,
    physics: {
      friction:           0.22,
      turnFactorLow:      0.34,
      turnFactorHigh:     0.50,
      handbrakeTurnBoost: 1.35,
      driftTargetScale:   0.65,
      driftLateralLerp:   6,
      driftYawBoost:      1.4,
      tractionGripFull:   0.84,
      tractionGripDrift:  0.08,
      reverseAccelScale:  0.45,
      wallRestitution:           0.25,
      obstacleRestitution:       1.75,   // COR = 0.75 — pinball-bouncy feel
      wallHeadOnPenalty:         0.32,
      wallGlancingThreshold:     0.25,
      obstacleHeadOnPenalty:     0.12,   // less speed loss; momentum is mostly redirected
      obstacleGlancingThreshold: 0.20,   // wider glancing zone; shallow contact slides off
      minimumObstacleBounceMps:  1.0,    // floor for outward speed — prevents low-speed sticking
      referenceMass:         8,
      carCarRestitution:         1.3,    // COR = 0.3 — soft bump, not pinball
      carCarHeadOnPenalty:       0.10,
      carCarGlancingThreshold:   0.25,
    },
  },

  hostWeaponOverrideTemplate: {
    damage:          18,
    reloadMs:        600,
    projectileSpeed: 90,
  },

  // ── WIRED ─────────────────────────────────────────────────────────────────────
  boost: {
    maxEnergy:      100,
    drainRate:       40,
    regenRate:       20,
    speedMultiplier:  1.5,
    accelMultiplier:  2.0,
  },

  audio: {
    engineMasterGain:   0.85,
    baseRpm:            2500,
    idleRpm:             800,
    maxRpm:             6500,
    loadBoost:           600,
    rpmSmooth:          0.08,
    gearUpThresholds:   [0.28, 0.53, 0.78],
    gearDnThresholds:   [0.25, 0.50, 0.75],
    gearRpmDrop:        0.7,
    gearDipMs:          100,
    gearDipGain:        0.55,
    playbackMin:        0.5,
    playbackMax:        3.0,
    filterFreqIdle:     800,
    filterMaxFreq:      5000,
    filterThrottleBoost: 800,
    filterSmooth:       0.06,
    coastGainScale:     0.75,
    tyreRateBase:       0.8,
    tyreRateRange:      0.4,
    squealCooldownMs:   800,
    squealDeltaThresh:  0.3,
  },

  camera: {
    firstPersonHeight:  0.85,
    firstPersonForward: 0.2,
  },
};
